import { useMemo } from 'react';
import {
  ActionableNotification,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import { useAtom } from 'jotai';
import {
  chartCashflowPeriodAtom,
  chartDimensionsAtom,
  chartMetricAtom,
} from './atoms';
import type { ChartExportDescriptor } from './chart-export-registry';
import { useRollupCardContext, useVisualSummary } from './hooks';
import { ApiEnvelopeError } from 'lib/evaluation-case/api-envelope';
import { VisualSummaryChart } from './visual-summary-chart';
import type {
  RollupDimensionOption,
  RollupMetricOption,
  RollupOptions,
  VisualSummaryRow,
} from './types';
import styles from './visual-summary-chart-card.module.scss';

// Stable empty fallbacks so a "no options / no data yet" render doesn't mint fresh array
// identities that would churn the chart's memos + `renderData`.
const EMPTY_DIMENSIONS: string[] = [];
const EMPTY_OPTIONS: RollupDimensionOption[] = [];
const EMPTY_METRIC_OPTIONS: RollupMetricOption[] = [];
const EMPTY_SERIES: VisualSummaryRow[] = [];

// This chart's content-export identity — its PNG lands in the ZIP as `charts[id]`
// (distinct basename from the table's `evaluation_output_aggregation.xlsx`). The section
// is kept mounted (rendered off-screen when hidden) so this SciChart chart always registers.
const EXPORT_DESCRIPTOR: ChartExportDescriptor = {
  id: 'evaluation_output_aggregation_chart',
  name: 'Evaluation Output Aggregation',
  group: 'valueRollup',
};

export interface VisualSummaryChartCardProps {
  /** The shared options catalog (fetched once by the section shell), or undefined until it resolves. */
  options: RollupOptions | undefined;
  /** Whether the shared catalog is still loading (folds into the chart's loading umbrella). */
  optionsLoading: boolean;
}

/**
 * Connected Visual Summary CHART card — subscribes to the CHART view-state atoms
 * (dimensions / metric / period), runs `useVisualSummary`, and drives the presentational
 * `VisualSummaryChart`. Split out of the section (from the table card) so a chart-only
 * change re-renders only this card — it does NOT read the table's atoms, so a table
 * change never touches it. Its Group-by / metric default to the SHARED config defaults
 * (`options.defaults`), which the table also uses, so the two cards start on the same
 * defaults without any cross-card coupling. Only the shared filters / spatial scope
 * re-render both cards. Its data error shows above the card.
 *
 * @param {VisualSummaryChartCardProps} props - The shared options catalog + its loading flag.
 * @return {JSX.Element} The Visual Summary chart card.
 */
export function VisualSummaryChartCard({
  options,
  optionsLoading,
}: VisualSummaryChartCardProps) {
  const {
    packageId,
    calculationId,
    status,
    isRunning,
    filters,
    mapScope,
    nameForAsset,
  } = useRollupCardContext();

  // Chart view-state — INDEPENDENT of the table's (FR#12/#13): its own dims / metric /
  // period, each defaulting to the SHARED config defaults (`options.defaults`, from
  // `useRollupOptions`) when unset — so the chart and table start on the same defaults
  // without this card reading the table's atoms (no cross-card coupling / re-render).
  const [chartDimsAtomValue, setChartDimensions] = useAtom(chartDimensionsAtom);
  const [chartMetricAtomValue, setChartMetric] = useAtom(chartMetricAtom);
  const [chartPeriod, setChartPeriod] = useAtom(chartCashflowPeriodAtom);

  const effectiveChartDimensions = useMemo(
    () =>
      chartDimsAtomValue ?? options?.defaults.dimensions ?? EMPTY_DIMENSIONS,
    [chartDimsAtomValue, options?.defaults.dimensions]
  );
  const effectiveChartMetric =
    chartMetricAtomValue ?? options?.defaults.metric ?? '';

  const {
    data: visualSummaryRows,
    isFetching,
    error,
    refetch,
  } = useVisualSummary({
    calcId: calculationId,
    packageId,
    status,
    dimensions: effectiveChartDimensions,
    metric: effectiveChartMetric,
    cashflowPeriod: chartPeriod,
    filters,
    mapScope,
  });

  // `series` is fed straight from the React Query `data` (a stable ref between renders),
  // blanked when the chart is disabled (no dims / no metric) OR there's no calc (workspace
  // / draft) so a `keepPreviousData` hold doesn't linger as stale data and `renderData`
  // doesn't re-run on identity churn.
  const series = useMemo(() => {
    if (
      calculationId === null ||
      effectiveChartDimensions.length === 0 ||
      effectiveChartMetric === ''
    ) {
      return EMPTY_SERIES;
    }
    return visualSummaryRows ?? EMPTY_SERIES;
  }, [
    visualSummaryRows,
    effectiveChartDimensions,
    effectiveChartMetric,
    calculationId,
  ]);
  const groupBy = useMemo(
    () => ({
      options: options?.dimensions ?? EMPTY_OPTIONS,
      selected: effectiveChartDimensions,
      onChange: setChartDimensions,
    }),
    [options?.dimensions, effectiveChartDimensions, setChartDimensions]
  );
  const metric = useMemo(
    () => ({
      options: options?.metrics ?? EMPTY_METRIC_OPTIONS,
      value: effectiveChartMetric,
      onChange: setChartMetric,
    }),
    [options?.metrics, effectiveChartMetric, setChartMetric]
  );
  const period = useMemo(
    () => ({
      value: chartPeriod,
      onApply: setChartPeriod,
      onRemove: () => setChartPeriod(null),
    }),
    [chartPeriod, setChartPeriod]
  );

  // Loading umbrella = the shared catalog OR this card's own fetch OR the calc-lifecycle
  // wait. This card surfaces only its OWN data error; suppressed while loading.
  const isLoading = optionsLoading || isFetching || isRunning;
  const showError = Boolean(error) && !isLoading;

  // INS-12433 — see ValueRollupTableCard beside it: a permanent failure keeps the banner
  // but loses the Retry, because retrying a calculation whose snapshot was never written
  // can only fail again. Anything untyped stays retryable.
  const canRetry = !(error instanceof ApiEnvelopeError && !error.retryable);

  return (
    <>
      {showError && error && canRetry && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the visual summary"
          subtitle={error.message}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}

      {/* Permanent failure: same placement, no Retry. role="alert" so the branch the user
    cannot act on is not announced more quietly than the one they can. */}
      {showError && error && !canRetry && (
        <InlineNotification
          className={styles.banner}
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title="Couldn't load the visual summary"
          subtitle={error.message}
        />
      )}

      <div className={styles.card}>
        <div className={styles.headingRow}>
          <h3 className={styles.cardTitle}>Visual Summary</h3>
          {isLoading && (
            <InlineLoading
              className={styles.headingSpinner}
              description=""
              aria-hidden
            />
          )}
        </div>
        <VisualSummaryChart
          series={series}
          isLoading={isLoading}
          groupBy={groupBy}
          metric={metric}
          period={period}
          registerExport={EXPORT_DESCRIPTOR}
          nameForAsset={nameForAsset}
        />
      </div>
    </>
  );
}
